<template>
  <div class="dashboard-body">
    <div class="row">
      <sidenav class="custom-col-width-2" />
      <div class="custom-col-width-10">
        <navbar />
        <div class="content-section">
          <deleteConfirmationModal v-if="showDeleteConfirmationModal" :delete-function="deleteUser"
            @close-delete-confirmation-modal="showDeleteConfirmationModal = false"
            @refresh-records="getUsers()" />
          <addUserModal v-if="showAddUserModal"
            :seat-limit-reached="seatLimitReached"
            :seat-allowance="seatAllowance"
            @close-add-user-modal="showAddUserModal = false"
            @refresh-users="refreshAfterAdd" />
          <section class="list-page">
            <header class="list-page__head">
              <div class="list-page__heading">
                <h2 class="list-page__title">Users</h2>
                <p class="list-page__count">{{ userCountLabel }}</p>
              </div>
              <button type="button" class="list-page__action" v-on:click="showAddUserModal = true">
                <i class="pi pi-plus"></i>
                <span>New User</span>
              </button>
            </header>

            <div class="list-page__table">
              <tableLoader v-if="usersIsLoading" message="Loading accounts…" />
              <div v-if="!usersIsLoading" class="table-scroller">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Name</th>
                      <th scope="col">Email</th>
                      <th scope="col">Phone</th>
                      <th scope="col">Role</th>
                      <th scope="col">Status</th>
                      <th scope="col">Join Date</th>
                      <th scope="col" class="col-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(user, index) in users" v-bind:key="user.id || index">
                      <th scope="row">{{ index + 1 }}</th>
                      <td class="row-name">{{ $filters.fullName(user.fname, user.lname) }}</td>
                      <td>
                        <a v-if="user.email" :href="`mailto:${user.email}`">{{ user.email }}</a>
                        <span v-else class="row-muted">-</span>
                      </td>
                      <td>
                        <a v-if="user.phoneNumber" :href="`tel:${user.phoneNumber}`">{{ user.phoneNumber }}</a>
                        <span v-else class="row-muted">-</span>
                      </td>
                      <td><span class="badge badge-secondary">{{ user.role }}</span></td>
                      <td>
                        <span v-if="user.isActive" class="badge badge-success">Active</span>
                        <span v-else class="badge badge-danger">Inactive</span>
                      </td>
                      <td>{{ $filters.getDateOnly(user.createdAt) }}</td>
                      <td class="col-actions">
                        <div class="dropdown dropleft">
                          <div class="dropdown-toggle actions-dropdown" type="button" data-toggle="dropdown">
                            <i class="pi pi-ellipsis-h"></i>
                          </div>
                          <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
                            <a class="dropdown-item final-item text-danger" v-on:click="toggleDeleteConfirmationModal(user)">Remove</a>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="!usersIsLoading && (!users || users.length === 0)" class="list-page__empty">
                <i class="pi pi-user"></i>
                <div>No users found.</div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import sidenav from "@/components/Sidenav.vue";
import navbar from "@/components/Navbar.vue";
import addUserModal from "@/components/user/Add_User_Modal.vue";
import deleteConfirmationModal from "@/components/Delete_Confirmation_Modal.vue";
import tableLoader from "@/components/Table_Loader.vue";
import { DELETE_USER, GET_USERS, GET_SUBSCRIPTIONS } from "@/store/action_types";

export default {
  title: "Users",
  components: {
    sidenav,
    navbar,
    addUserModal,
    deleteConfirmationModal,
    tableLoader,
  },
  data() {
    return {
      showAddUserModal: false,
      showDeleteConfirmationModal: false,
      selectedUser: null,
    };
  },
  computed: {
    users() {
      return this.$store.getters.users.data;
    },
    usersIsLoading() {
      return this.$store.getters.users.isLoading;
    },
    userCount() {
      return (this.users || []).length;
    },
    userCountLabel() {
      const total = this.userCount;
      return total === 1 ? "1 user" : `${total} users`;
    },
    // The org's active (non-pending) subscription, used to read the seat limit.
    activeSubscription() {
      const subs = this.$store.getters.subscriptions.data || [];
      return subs.find((s) => s.isActive && !s.isPending) || null;
    },
    subscriptionsReady() {
      const sub = this.$store.getters.subscriptions;
      return !sub.isLoading && sub.data != null;
    },
    // Mirrors the backend's resolveSeatAllowance: no active plan -> 1 seat;
    // a null maxUsers on the plan -> unlimited (null).
    seatAllowance() {
      if (!this.activeSubscription || !this.activeSubscription.subscriptionPlan) return 1;
      const max = this.activeSubscription.subscriptionPlan.maxUsers;
      return max == null ? null : Number(max);
    },
    seatLimitReached() {
      // Don't gate until we actually know the plan's allowance.
      if (!this.subscriptionsReady) return false;
      return this.seatAllowance != null && this.userCount >= this.seatAllowance;
    },
  },
  methods: {
    toggleDeleteConfirmationModal(user) {
      this.selectedUser = user;
      this.showDeleteConfirmationModal = true;
    },
    deleteUser() {
      return this.$store.dispatch(DELETE_USER, this.selectedUser.id);
    },
    getUsers() {
      this.$store.dispatch(GET_USERS);
    },
    getSubscriptions() {
      this.$store.dispatch(GET_SUBSCRIPTIONS, { limit: 100 });
    },
    refreshAfterAdd() {
      // A new member changes the seat count, so refresh the list (the gate
      // recomputes from userCount).
      this.getUsers();
    },
  },
  mounted() {
    this.getUsers();
    this.getSubscriptions();
  },
};
</script>
